import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useSite } from "@/lib/site";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import PostCard, { formatDate } from "@/components/PostCard";
import Splash from "@/components/Splash";
import Seo, { articleJsonLd } from "@/components/Seo";
import NotFound from "@/pages/NotFound";
import Img from "@/components/Img";

export default function NewsArticle() {
  const { slug } = useParams();
  const { t, L, lang } = useLang();
  const site = useSite();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let active = true;
    setPost(null);
    setMissing(false);
    api
      .get(`/posts/${slug}`)
      .then((r) => active && setPost(r.data))
      .catch(() => active && setMissing(true));
    return () => {
      active = false;
    };
  }, [slug]);

  if (missing) return <NotFound />;
  if (!post) return <Splash />;

  const all = site?.posts || [];
  const related = all.filter((p) => p.slug !== slug && p.category === post.category).slice(0, 3);
  const paragraphs = L(post.body).split("\n\n").filter(Boolean);

  return (
    <article data-testid="news-article-page">
      <Seo
        type="article"
        title={L(post.seo_title) || L(post.title)}
        description={L(post.seo_description) || L(post.excerpt)}
        image={post.image}
        jsonLd={articleJsonLd(post, site?.settings, L)}
      />
      <header className="relative flex min-h-[62vh] items-end overflow-hidden">
        <Img src={post.image} width={1920} sizes="100vw" eager alt={L(post.title)} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "var(--hero-overlay)" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/55 to-background/10" />
        <div className="relative mx-auto w-full max-w-4xl px-5 pb-14 pt-32 sm:px-8">
          <Link to="/news" data-testid="article-back-link" className="mb-7 inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-primary">
            <ArrowLeft className="h-3.5 w-3.5 rtl:rotate-180" /> {t("news_back")}
          </Link>
          <p className="eyebrow mb-4">
            {t(`cat_${post.category}`)} — {formatDate(post.date, lang)}
          </p>
          <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="article-title">
            {L(post.title)}
          </h1>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-5 py-16 sm:px-8 md:py-24">
        <Reveal>
          <p className="border-s-2 border-primary ps-6 font-head text-xl md:text-2xl leading-snug text-foreground/90" data-testid="article-excerpt">
            {L(post.excerpt)}
          </p>
        </Reveal>
        <div className="mt-10 space-y-6" data-testid="article-body">
          {paragraphs.map((p, i) => (
            <Reveal key={i} delay={0.04 * i}>
              <p className="text-base sm:text-lg text-muted-foreground leading-[1.85]">{p}</p>
            </Reveal>
          ))}
        </div>
      </div>

      {related.length > 0 && (
        <section className="border-t border-border bg-card/25">
          <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 md:py-24">
            <div className="mb-10 flex flex-wrap items-baseline justify-between gap-4">
              <h2 className="font-head text-3xl md:text-4xl">{t("news_related")}</h2>
              <Link to="/news" data-testid="article-view-all" className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-primary">
                {t("news_view_all")} <ArrowRight className="h-3.5 w-3.5 rtl:rotate-180" />
              </Link>
            </div>
            <div className="grid gap-7 md:grid-cols-3" data-testid="article-related-grid">
              {related.map((p, i) => (
                <Reveal key={p.id} delay={0.06 * i}>
                  <PostCard post={p} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
